import { useMemo } from 'react'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useGuildMembers } from 'app/frontend/base/config/queries'

import Select from 'app/frontend/base/components/Select'
import { Link } from 'react-router-dom'
import { CharacterClassIcon } from 'app/frontend/base/components'
import {
  ColumnDef,
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Check } from 'lucide-react'

export type EditableTrackingRule = Partial<
  wowaudit.TrackingRule & {
    _destroy?: boolean
  }
> & { rank: number }

export const extendTrackingRules = (rules: wowaudit.TrackingRule[]) => {
  return Array.from({ length: 10 }, (_, i) => i).map((rank) => {
    const existingRule = rules?.find((rule) => rule.rank === rank)
    return {
      id: existingRule?.id,
      rank,
      teamRankId: existingRule?.teamRankId,
      _destroy: existingRule ? false : undefined,
    }
  })
}

const TrackingRules = () => {
  const { team, guild } = useTeam()
  const { data: guildMembers, isPending: loadingGuildMembers } =
    useGuildMembers(guild.id)
  const { setValue } = useFormContext()

  const trackingRulesAttributes = useWatch({
    name: 'tracking_rules_attributes',
  }) as EditableTrackingRule[]

  const rankSamples = useMemo(() => {
    if (!guildMembers) return {}

    return guildMembers.reduce((samples, member) => {
      if (member.rankInGuild !== undefined && !samples[member.rankInGuild]) {
        samples[member.rankInGuild] = member
      }
      return samples
    }, {} as Record<number, wowaudit.CharacterReference>)
  }, [guildMembers])

  const rankOptions = useMemo(() => {
    return (
      team.teamRanks?.map((rank) => ({
        label: rank.name || `Rank ${rank.id}`,
        value: rank.id.toString(),
      })) ?? []
    )
  }, [team.teamRanks])

  const columns: ColumnDef<EditableTrackingRule>[] = useMemo(
    () => [
      {
        id: 'select',
        header: '',
        size: 30,
        cell: ({ row }) => (
          <div
            className={`h-4 w-4 shrink-0 rounded-[4px] border flex items-center justify-center ${
              row.original._destroy === false
                ? 'bg-primary border-primary text-primary-foreground'
                : 'bg-accent'
            }`}
          >
            {row.original._destroy === false && <Check size={14} />}
          </div>
        ),
      },
      {
        id: 'rank',
        header: 'Rank',
        accessorKey: 'rankName',
        cell: ({ row }) => {
          return (
            <div
              className={`h-8 flex items-center whitespace-nowrap ${
                row.original._destroy === false ? 'text-primary' : ''
              }`}
            >
              {row.original.rank === 0
                ? 'Guild Master'
                : `Rank ${row.original.rank + 1}`}
            </div>
          )
        },
      },
      {
        id: 'sample',
        header: 'Sample',
        size: 180,
        cell: ({ row }) => {
          if (loadingGuildMembers) return null

          const sample = rankSamples[row.original.rank]
          return sample ? (
            <Link
              to={sample.path}
              className='text-foreground hover:text-primary inline-flex items-center gap-2 mt-1'
              target='_blank'
              onClick={(e) => e.stopPropagation()}
            >
              <CharacterClassIcon
                characterClass={sample.characterClass}
                size={'small'}
              />
              {sample.name}
            </Link>
          ) : (
            <span className='italic text-muted-foreground'>N/A</span>
          )
        },
      },
      {
        id: 'teamRank',
        header: 'Add as',
        size: 250,
        cell: ({ row }) => {
          return (
            <Select
              value={row.original.teamRankId?.toString()}
              setValue={(value) =>
                setValue(
                  `tracking_rules_attributes.${row.original.rank}.teamRankId`,
                  value ? Number(value) : undefined,
                )
              }
              options={rankOptions}
              onClick={(e) => e.stopPropagation()}
              placeholder='Default'
              isClearable
              className='w-full h-8'
            />
          )
        },
      },
    ],
    [rankOptions, loadingGuildMembers],
  )

  const table = useReactTable({
    data: trackingRulesAttributes,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      size: undefined,
    },
    getRowId: (row) => row.rank.toString(),
  })

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                style={{
                  minWidth: header.column.columnDef.size,
                  maxWidth: header.column.columnDef.size,
                  width: header.column.columnDef.size,
                }}
              >
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext(),
                )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow
            key={row.id}
            data-state={row.original._destroy === false ? 'selected' : ''}
            onClick={() => {
              setValue(
                `tracking_rules_attributes.${row.original.rank}._destroy`,
                row.original._destroy === undefined
                  ? false
                  : !row.original._destroy,
              )
            }}
            className='opacity-50 data-[state=selected]:opacity-100 cursor-pointer'
          >
            {row.getVisibleCells().map((cell) => (
              <TableCell
                key={cell.id}
                className='py-1'
                style={{
                  minWidth: cell.column.columnDef.size,
                  maxWidth: cell.column.columnDef.size,
                  width: cell.column.columnDef.size,
                }}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

export default TrackingRules
